import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import {
  ReFulfillmentBillingConfig,
  ReFulfillmentBillingMode,
  TeamType,
} from "@redotech/redo-model/team";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import {
  groupInput,
  input,
  InputProvider,
  numberValidator,
} from "@redotech/ui/form";
import { memo, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { automationUrl } from "../merchant-app";
import { LoadTeamContext, TeamContext } from "../team";
import * as styles from "./logistics.module.css";
import { LocationsCard } from "./logistics/locations-card";
import { LogisticsProductSummary } from "./logistics/logistics-product-summary";
import { SettingCard } from "./returns-package-protection/v2/component/setting-card";
import { SettingSection } from "./returns-package-protection/v2/component/setting-section";
import { SettingSectionTabNav } from "./returns-package-protection/v2/component/setting-section-tab-nav";
import {
  redoManagedReturnsFormValueToTeam,
  RedoManagedReturnsSettingsCardV2,
  redoManagedReturnsSettingsDefault,
  redoManagedReturnsSettingsForm,
  redoManagedReturnsTeamToFormValue,
} from "./returns-package-protection/v2/redo-managed-returns-settings";
import {
  TabNavigationContext,
  TabNavigationProvider,
} from "./returns-package-protection/v2/tab-navigation-provider";

const reFulfillmentBillingConfigForm = groupInput({
  billingMode: input<"percentage" | "fixed">(),
  revSharePercentage: input<number>(),
  fixedFeeAmount: input<string>({ validator: numberValidator({ min: 0 }) }),
});

const reFulfillmentForm = groupInput({
  enabled: input<boolean>(),
  redoOwned: reFulfillmentBillingConfigForm,
  merchantOwned: reFulfillmentBillingConfigForm,
});

type ReFulfillmentValue = InputProvider.Value<typeof reFulfillmentForm>;

const billingConfigDefault: InputProvider.Value<
  typeof reFulfillmentBillingConfigForm
> = { billingMode: "percentage", revSharePercentage: 50, fixedFeeAmount: "0" };

const reFulfillmentDefault: ReFulfillmentValue = {
  enabled: false,
  redoOwned: billingConfigDefault,
  merchantOwned: billingConfigDefault,
};

interface SkippedAutomationInfo {
  locationName: string;
  automationId: string;
  threePlName: string;
}

const logisticsForm = groupInput(
  {
    redoManagedReturnsSettings: redoManagedReturnsSettingsForm,
    reFulfillment: reFulfillmentForm,
    customsBrokerClientNumber: input<string>(),
  },
  {
    validator: (value) => {
      if (
        value.redoManagedReturnsSettings.enabled &&
        value.redoManagedReturnsSettings.availableReturnsCenters.length === 0
      ) {
        return [
          "At least one returns center must be configured when Redo Managed Returns is enabled",
        ];
      }
      return [];
    },
  },
);

type LogisticsValue = InputProvider.Value<typeof logisticsForm>;

const logisticsDefault: LogisticsValue = {
  redoManagedReturnsSettings: redoManagedReturnsSettingsDefault,
  reFulfillment: reFulfillmentDefault,
  customsBrokerClientNumber: "",
};

export const LogisticsPage = memo(function LogisticsPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);
  const client = useRequiredContext(RedoAdminClientContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const { value: automationsData } = useRpcData(
    rpcClient,
    "getRmrAutomations",
    { input: { teamId } },
  );

  const { value: returnsCentersData } = useRpcData(
    rpcClient,
    "getReturnsCenters",
    { input: { sort: null } },
  );

  const automationByReturnsCenterId = useMemo(() => {
    const automationByAccountId = new Map<
      string,
      { automationId: string; threePlName: string }
    >();
    for (const {
      thirdPartyAccountId,
      automationId,
      threePlName,
    } of automationsData?.automations ?? []) {
      automationByAccountId.set(thirdPartyAccountId, {
        automationId,
        threePlName,
      });
    }

    const map = new Map<
      string,
      { automationId: string; threePlName: string; locationName: string }
    >();
    for (const center of returnsCentersData?.returnsCenters ?? []) {
      if (!center.thirdPartyAccountId || !center.locationName) continue;
      const automation = automationByAccountId.get(center.thirdPartyAccountId);
      if (automation) {
        map.set(center.id, {
          ...automation,
          locationName: center.locationName,
        });
      }
    }
    return map;
  }, [automationsData, returnsCentersData]);

  const [skippedAutomations, setSkippedAutomations] = useState<
    SkippedAutomationInfo[] | null
  >(null);

  const value: LogisticsValue | undefined = useMemo(() => {
    if (!team) {
      return;
    }

    return {
      redoManagedReturnsSettings: redoManagedReturnsTeamToFormValue(team),
      reFulfillment: {
        enabled: !!team.settings.reFulfillment?.enabled,
        redoOwned: billingConfigFromTeam(
          team.settings.reFulfillment?.redoOwned,
        ),
        merchantOwned: billingConfigFromTeam(
          team.settings.reFulfillment?.merchantOwned,
        ),
      },
      customsBrokerClientNumber: team.customsBrokerClientNumber ?? "",
    };
  }, [team]);

  const input = useInput(logisticsForm, value || logisticsDefault);

  const handleCloseSkipped = useHandler(() => setSkippedAutomations(null));

  const coveragePriceLimit = useMemo(() => {
    const coveragePrice = parseFloat(team?.pricePerOrder || "0") || 0;
    const merchantBilledPrice =
      parseFloat(team?.merchantPricePerOrder || "0") || 0;
    return Math.max(coveragePrice, merchantBilledPrice);
  }, [team?.pricePerOrder, team?.merchantPricePerOrder]);

  const save: PageFormSave = useHandler(async (signal) => {
    const formValue = input.value;
    const totalCoverageShare =
      (Number(formValue.redoManagedReturnsSettings.processingCoverageShare) ||
        0) +
      (Number(
        formValue.redoManagedReturnsSettings.consolidationCoverageShare,
      ) || 0);
    if (totalCoverageShare > coveragePriceLimit) {
      return;
    }

    const currentTeam = await getTeam(client, { teamId, signal });

    const previouslyEnabledIds = new Set(
      redoManagedReturnsTeamToFormValue(currentTeam)
        .availableReturnsCenters.filter((c) => c.refulfillment)
        .map((c) => c.returnsCenterId),
    );

    const skipped: SkippedAutomationInfo[] = [];
    for (const center of formValue.redoManagedReturnsSettings
      .availableReturnsCenters) {
      if (
        center.refulfillment &&
        !previouslyEnabledIds.has(center.returnsCenterId)
      ) {
        const automation = automationByReturnsCenterId.get(
          center.returnsCenterId,
        );
        if (automation) {
          skipped.push({
            locationName: automation.locationName,
            automationId: automation.automationId,
            threePlName: automation.threePlName,
          });
        }
      }
    }

    currentTeam.settings.redoManagedReturns = redoManagedReturnsFormValueToTeam(
      formValue.redoManagedReturnsSettings,
    );

    currentTeam.settings.reFulfillment = {
      ...currentTeam.settings.reFulfillment,
      enabled: formValue.reFulfillment.enabled,
      redoOwned: billingConfigToTeam(formValue.reFulfillment.redoOwned),
      merchantOwned: billingConfigToTeam(formValue.reFulfillment.merchantOwned),
    };

    currentTeam.customsBrokerClientNumber =
      formValue.customsBrokerClientNumber || undefined;

    await updateTeam(client, { teamId, team: currentTeam, signal });

    loadTeam();
    if (skipped.length > 0) {
      setSkippedAutomations(skipped);
    }
  });

  return (
    <>
      {value && (
        <LogisticsForm id={teamId} input={input} save={save} value={value} />
      )}
      {skippedAutomations && skippedAutomations.length > 0 && (
        <SkippedAutomationsModal
          onClose={handleCloseSkipped}
          skippedAutomations={skippedAutomations}
          teamId={teamId}
        />
      )}
    </>
  );
});

const LogisticsForm = memo(function LogisticsForm({
  id,
  input,
  save,
  value,
}: {
  id: string;
  input: InputProvider.Form<typeof logisticsForm>;
  save: PageFormSave;
  value: LogisticsValue | undefined;
}) {
  const team = useContext(TeamContext);
  const is3pl = team?.teamType === TeamType.FulfillmentService;

  const coveragePriceLimit = useMemo(() => {
    const coveragePrice = parseFloat(team?.pricePerOrder || "0") || 0;
    const merchantBilledPrice =
      parseFloat(team?.merchantPricePerOrder || "0") || 0;
    return Math.max(coveragePrice, merchantBilledPrice);
  }, [team?.pricePerOrder, team?.merchantPricePerOrder]);

  const {
    redoManagedReturnsSettings,
    reFulfillment,
    customsBrokerClientNumber,
  } = input.inputs;

  const rmrErrors = useMemo(() => {
    const errors = [...input.allErrors];
    const totalShare =
      (Number(redoManagedReturnsSettings.value.processingCoverageShare) || 0) +
      (Number(redoManagedReturnsSettings.value.consolidationCoverageShare) ||
        0);
    if (totalShare > coveragePriceLimit) {
      errors.push(
        `Coverage shares ($${totalShare.toFixed(2)}) exceed coverage limit ($${coveragePriceLimit.toFixed(2)})`,
      );
    }
    return errors;
  }, [
    input.allErrors,
    redoManagedReturnsSettings.value.processingCoverageShare,
    redoManagedReturnsSettings.value.consolidationCoverageShare,
    coveragePriceLimit,
  ]);

  return (
    <TabNavigationProvider>
      <div className={styles.pageContainer}>
        <SettingSectionTabNav />
        <div className={styles.settingsContent}>
          <ErrorBanner />
          <PageForm
            initial={value || logisticsDefault}
            input={input}
            save={save}
          >
            <SettingSection description="" id="products" title="Products">
              <SettingCard>
                <Flex align="stretch" dir="column" gap="none">
                  <LogisticsProductSummary
                    description="Charge merchants for managed returns processing"
                    productEnabled={redoManagedReturnsSettings.value.enabled}
                    setProductEnabled={
                      redoManagedReturnsSettings.inputs.enabled.setValue
                    }
                    title="Redo Managed Returns"
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
            <SettingSection
              collapsible
              description="Managed returns processing, billing, and returns centers"
              errors={rmrErrors}
              id="redo-managed-returns"
              title="Redo Managed Returns"
            >
              <RedoManagedReturnsSettingsCardV2
                coveragePriceLimit={coveragePriceLimit}
                input={redoManagedReturnsSettings}
                teamId={id}
              />
            </SettingSection>
            <SettingSection
              collapsible
              defaultCollapsed
              description="Billing for items fulfilled from Returns Centers"
              id="re-fulfillment"
              title="Re-fulfillment"
            >
              <ReFulfillmentBillingCard
                disabled={!reFulfillment.value.enabled}
                merchantOwned={reFulfillment.inputs.merchantOwned}
                redoOwned={reFulfillment.inputs.redoOwned}
              />
            </SettingSection>
            <SettingSection
              collapsible
              defaultCollapsed
              description="Customs and cross-border settings"
              id="cross-border"
              title="Cross-Border"
            >
              <SettingCard>
                <Flex dir="column" gap="lg" p="xl">
                  <RedoTextInput
                    label="Customs Broker Client Number"
                    setValue={customsBrokerClientNumber.setValue}
                    value={customsBrokerClientNumber.value}
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
          </PageForm>
          {is3pl && (
            <SettingSection
              collapsible
              defaultCollapsed
              description="Manage which locations are designated as returns centers"
              id="locations"
              title="Locations"
            >
              <LocationsCard teamId={id} />
            </SettingSection>
          )}
        </div>
      </div>
    </TabNavigationProvider>
  );
});

function billingConfigFromTeam(
  config: ReFulfillmentBillingConfig | undefined,
): InputProvider.Value<typeof reFulfillmentBillingConfigForm> {
  return {
    billingMode: config?.billingMode ?? "percentage",
    revSharePercentage: config?.revSharePercentage ?? 50,
    fixedFeeAmount: (config?.fixedFeeAmount ?? 0).toString(),
  };
}

function billingConfigToTeam(
  config: InputProvider.Value<typeof reFulfillmentBillingConfigForm>,
): ReFulfillmentBillingConfig {
  return {
    billingMode: config.billingMode as ReFulfillmentBillingMode,
    revSharePercentage: config.revSharePercentage,
    fixedFeeAmount: Number(config.fixedFeeAmount) || 0,
  };
}

const ReFulfillmentBillingSection = memo(function ReFulfillmentBillingSection({
  disabled,
  input,
}: {
  disabled: boolean;
  input: InputProvider.Form<typeof reFulfillmentBillingConfigForm>;
}) {
  const options: RadioGroupOption<"percentage" | "fixed">[] = useMemo(
    () => [
      {
        id: "percentage" as const,
        label: "Percentage of order value",
        childrenRenderLocation: "right" as const,
        children: (
          <RedoIncrementDecrement
            disabled={
              disabled || input.inputs.billingMode.value !== "percentage"
            }
            max={100}
            min={0}
            setValue={input.inputs.revSharePercentage.setValue}
            suffix="%"
            value={input.inputs.revSharePercentage.value}
          />
        ),
      },
      {
        id: "fixed" as const,
        label: "Fixed fee per fulfillment",
        childrenRenderLocation: "right" as const,
        children: (
          <RedoTextInput
            IconTrailing={() => <span>USD</span>}
            min={0}
            placeholder="0.00"
            setValue={input.inputs.fixedFeeAmount.setValue}
            size="sm"
            state={
              disabled || input.inputs.billingMode.value !== "fixed"
                ? "disabled"
                : "default"
            }
            step={0.01}
            value={input.inputs.fixedFeeAmount.value}
          />
        ),
      },
    ],
    [disabled, input.inputs],
  );

  return (
    <RedoRadioGroup<"percentage" | "fixed">
      options={options}
      setValue={input.inputs.billingMode.setValue}
      value={input.inputs.billingMode.value}
    />
  );
});

const CHEVRON_BASE_REFULFILLMENT: React.CSSProperties = {
  width: 20,
  height: 20,
  transition: "transform 0.2s ease",
  color: "var(--redo-colors-text-text-tertiary-600)",
  flexShrink: 0,
};

const ReFulfillmentBillingCard = memo(function ReFulfillmentBillingCard({
  disabled,
  redoOwned,
  merchantOwned,
}: {
  disabled: boolean;
  redoOwned: InputProvider.Form<typeof reFulfillmentBillingConfigForm>;
  merchantOwned: InputProvider.Form<typeof reFulfillmentBillingConfigForm>;
}) {
  const [redoOwnedOpen, setRedoOwnedOpen] = useState(true);
  const [merchantOwnedOpen, setMerchantOwnedOpen] = useState(true);

  return (
    <SettingCard>
      <div>
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setRedoOwnedOpen((v) => !v)}
          p="xl"
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Redo-owned inventory
          </Text>
          <ChevronRightIcon
            style={{
              ...CHEVRON_BASE_REFULFILLMENT,
              transform: redoOwnedOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </Flex>
        {redoOwnedOpen && (
          <Flex pb="xl" pl="xl" pr="xl">
            <ReFulfillmentBillingSection
              disabled={disabled}
              input={redoOwned}
            />
          </Flex>
        )}
      </div>
      <div>
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setMerchantOwnedOpen((v) => !v)}
          p="xl"
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Merchant-owned inventory
          </Text>
          <ChevronRightIcon
            style={{
              ...CHEVRON_BASE_REFULFILLMENT,
              transform: merchantOwnedOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </Flex>
        {merchantOwnedOpen && (
          <Flex pb="xl" pl="xl" pr="xl">
            <ReFulfillmentBillingSection
              disabled={disabled}
              input={merchantOwned}
            />
          </Flex>
        )}
      </div>
    </SettingCard>
  );
});

const ErrorBanner = memo(function ErrorBanner() {
  const { sectionErrors, sectionRefs } =
    useRequiredContext(TabNavigationContext);

  const allErrors = useMemo(() => {
    const errors: { section: string; sectionId: string; message: string }[] =
      [];
    for (const [id, sectionErrorList] of Object.entries(sectionErrors)) {
      const title = sectionRefs[id]?.title ?? id;
      for (const message of sectionErrorList) {
        errors.push({ section: title, sectionId: id, message });
      }
    }
    return errors;
  }, [sectionErrors, sectionRefs]);

  if (allErrors.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 100,
        maxWidth: 400,
      }}
    >
      <RedoAlert
        color="error"
        description={allErrors
          .map((e) => `${e.section}: ${e.message}`)
          .join(" | ")}
        title={`${allErrors.length} ${allErrors.length === 1 ? "error" : "errors"}`}
      />
    </div>
  );
});

const SkippedAutomationsModal = memo(function SkippedAutomationsModal({
  skippedAutomations,
  teamId,
  onClose,
}: {
  skippedAutomations: SkippedAutomationInfo[];
  teamId: string;
  onClose: () => void;
}) {
  const automationGroups = useMemo(() => {
    const map = new Map<
      string,
      { automationId: string; threePlName: string; locationNames: string[] }
    >();
    for (const {
      automationId,
      threePlName,
      locationName,
    } of skippedAutomations) {
      const existing = map.get(automationId);
      if (existing) {
        existing.locationNames.push(`${threePlName} - ${locationName}`);
      } else {
        map.set(automationId, {
          automationId,
          threePlName,
          locationNames: [`${threePlName} - ${locationName}`],
        });
      }
    }
    return [...map.values()];
  }, [skippedAutomations]);

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Dismiss", onClick: onClose, size: "xs" }}
      subtitle="New Shopify locations will be created by the background workflow. The automations below already exist and must be manually updated to include the new locations."
      title={
        <Flex align="center" gap="xs">
          <AlertTriangleIcon
            style={{
              color: "var(--redo-colors-warning-400)",
              flexShrink: 0,
              height: "1.1em",
              width: "1.1em",
            }}
          />
          <span>Some automations need manual update</span>
        </Flex>
      }
    >
      <Flex dir="column" gap="md">
        {automationGroups.map(
          ({ automationId, threePlName, locationNames }) => (
            <Flex dir="column" gap="xs" key={automationId}>
              <a
                href={automationUrl(teamId, automationId)}
                rel="noreferrer"
                style={{
                  alignItems: "center",
                  display: "inline-flex",
                  fontSize: "var(--redo-font-size-sm)",
                  fontWeight: "var(--redo-font-weight-medium)",
                  gap: "var(--redo-spacing-xs)",
                }}
                target="_blank"
              >
                {threePlName}
                <LinkExternal02Icon
                  style={{ height: "1em", width: "1em", flexShrink: 0 }}
                />
              </a>
              {locationNames.map((name) => (
                <Text fontSize="sm" key={name} textColor="tertiary">
                  • {name}
                </Text>
              ))}
            </Flex>
          ),
        )}
      </Flex>
    </RedoModal>
  );
});
